import { useCallback, useMemo } from 'react';
import { SetAuthorizationHeaderAsync } from '~/helpers/data.jsx';

const URL = `${window.env.apiEndpoint}/api/v1/CommitteeMemberships`;

// Reads the response body, unwrapping a JSON-encoded string when present.
const readMessage = async (response) => {
  let message = await response.text();
  try {
    message = JSON.parse(message);
  } catch {
    // body was plain text, not a JSON-encoded string — use it as-is.
  }
  return message || '';
};

// DevExtreme's FileManager shows an error's `errorText` property in its
// notification (otherwise it falls back to a generic "Unspecified error.").
const errorFromResponse = async (response) => {
  const message = (await readMessage(response)) || 'Operation failed.';
  const error = new Error(message);
  error.errorText = message;
  return error;
};

// API layer for committee documents (blobs stored under committees/{committeeId})
// exposed as the hooks pattern: returns memoized async functions scoped to the
// given committee. Functions resolve on success and throw an Error carrying
// `errorText` on failure. `upload` returns a result object so callers can drive
// the rename/replace dialog on a name clash.
export function useCommitteeDocuments(committeeId) {
  const authHeaders = useCallback(async () => {
    const o = {};
    await SetAuthorizationHeaderAsync(o);
    return o.headers;
  }, []);

  const listAttachments = useCallback(async (path) => {
    const params = new URLSearchParams({ Id: committeeId, path: path ?? '' });
    const response = await fetch(`${URL}/ListAttachments?${params.toString()}`, { headers: await authHeaders() });
    if (!response.ok) {
      throw await errorFromResponse(response);
    }
    return response.json();
  }, [committeeId, authHeaders]);

  // Returns { ok: true }, { fileExists: true, date } on a name clash, or throws.
  const upload = useCallback(async (fileData, fileName, path, replace) => {
    const formData = new FormData();
    formData.append('file', fileData, fileName);
    const params = new URLSearchParams({ Id: committeeId, path: path ?? '', replace: replace ? 'true' : 'false' });
    const response = await fetch(
      `${URL}/UploadAttachment?${params.toString()}`,
      { method: 'POST', body: formData, headers: await authHeaders() },
    );
    if (response.ok) {
      return { ok: true };
    }

    const message = await readMessage(response);
    if (typeof message === 'string' && message.includes('FileExists')) {
      return { ok: false, fileExists: true, date: message.split('|')[1] ?? '' };
    }

    const error = new Error(message || 'Upload failed.');
    error.errorText = message || 'Upload failed.';
    throw error;
  }, [committeeId, authHeaders]);

  const createFolder = useCallback(async (name, path) => {
    const params = new URLSearchParams({ Id: committeeId, name, path: path ?? '' });
    const response = await fetch(
      `${URL}/CreateFolder?${params.toString()}`,
      { method: 'POST', headers: await authHeaders() },
    );
    if (!response.ok) {
      throw await errorFromResponse(response);
    }
  }, [committeeId, authHeaders]);

  const deleteItem = useCallback(async (path, isDirectory) => {
    const params = new URLSearchParams({ Id: committeeId, path, isDirectory: isDirectory ? 'true' : 'false' });
    const response = await fetch(
      `${URL}/DeleteAttachment?${params.toString()}`,
      { method: 'DELETE', headers: await authHeaders() },
    );
    if (!response.ok) {
      throw await errorFromResponse(response);
    }
  }, [committeeId, authHeaders]);

  const rename = useCallback(async (path, newName, isDirectory) => {
    const params = new URLSearchParams({
      Id: committeeId, path, newName, isDirectory: isDirectory ? 'true' : 'false',
    });
    const response = await fetch(
      `${URL}/RenameAttachment?${params.toString()}`,
      { method: 'POST', headers: await authHeaders() },
    );
    if (!response.ok) {
      throw await errorFromResponse(response);
    }
  }, [committeeId, authHeaders]);

  const download = useCallback(async (fileName) => {
    const response = await fetch(
      `${URL}/DownloadAttachment?fileName=${encodeURIComponent(fileName)}`,
      { headers: await authHeaders() },
    );
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`);
    }
    return response.blob();
  }, [authHeaders]);

  return useMemo(
    () => ({ listAttachments, upload, createFolder, deleteItem, rename, download }),
    [listAttachments, upload, createFolder, deleteItem, rename, download],
  );
}
